import axios from 'axios';
import { parseUTCDate } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add JWT token to requests automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and notify the app to reopen login.
      // App.js listens for this and routes through its logout handler so the
      // login dialog reopens without a full page reload.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  verifyEmail: async (token) => {
    await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  }
};

// Users API
export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  update: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  updateLanguage: async (id, language) => {
    await api.patch(`/users/${id}/language`, JSON.stringify(language), {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  changeEmail: async (id, newEmail) => {
    await api.patch(`/users/${id}/email`, { newEmail });
  },

  getHashtags: async (userId) => {
    const response = await api.get(`/users/${userId}/hashtags`);
    return response.data;
  },

  updateLocationPreferences: async (userId, locationData) => {
    const response = await api.put(`/users/${userId}/location-preferences`, locationData);
    return response.data;
  },

  getFriends: async (userId) => {
    const response = await api.get(`/users/${userId}/friends`);
    return response.data;
  },

  getSystemHashtagDates: async (userId) => {
    const response = await api.get(`/users/${userId}/system-hashtag-dates`);
    return response.data;
  },

  getCommunityWorkLastMonth: async (userId) => {
    const response = await api.get(`/users/${userId}/community-work-last-month`);
    return response.data;
  },

  updateAvatarType: async (userId, avatarType) => {
    const user = await usersAPI.getById(userId);
    user.avatarType = avatarType;
    const response = await api.put(`/users/${userId}`, user);
    return response.data;
  },

  updateAvatarPicture: async (userId, pictureId) => {
    const response = await api.put(`/users/${userId}/avatar-picture`, pictureId);
    return response.data;
  },

  getUserDetails: async (userId) => {
    const response = await api.get(`/users/${userId}/details`);
    return response.data;
  },

  getUserHashtags: async (userId) => {
    const response = await api.get(`/users/${userId}/hashtags`);
    return response.data;
  },

  getEventCompanions: async (userId) => {
    const response = await api.get(`/users/${userId}/event-companions`);
    return response.data;
  },

  gdprDelete: async (userId) => {
    const response = await api.delete(`/users/${userId}/gdpr-delete`);
    return response.data;
  },

  gdprExport: async (userId) => {
    const response = await api.get(`/users/${userId}/gdpr-export`);
    return response.data;
  },

  resendVerificationEmail: async (userId) => {
    await api.post(`/users/${userId}/resend-verification`);
  }
};

// Events API
export const eventsAPI = {
  getAll: async () => {
    const response = await api.get('/events');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },

  getUserEvents: async (userId) => {
    const response = await api.get(`/events/user/${userId}`);
    return response.data;
  },

  getUserPastEvents: async (userId, days = null) => {
    const params = days != null ? { params: { days } } : {};
    const response = await api.get(`/events/user/${userId}/past`, params);
    return response.data;
  },

  getUserOngoingEvents: async (userId) => {
    const response = await api.get(`/events/user/${userId}/ongoing`);
    return response.data;
  },

  getNearbyPast: async (latitude, longitude, radiusKm = 10) => {
    const response = await api.get('/events/nearby/past', { params: { latitude, longitude, radiusKm } });
    return response.data;
  },

  create: async (eventData) => {
    const formData = new FormData();
    formData.append('name', eventData.name);
    formData.append('address', eventData.address);
    formData.append('latitude', eventData.latitude?.toString() || '');
    formData.append('longitude', eventData.longitude?.toString() || '');
    formData.append('date', eventData.date);
    formData.append('time', eventData.time);

    // Add participantIds array
    if (eventData.participantIds && eventData.participantIds.length > 0) {
      eventData.participantIds.forEach((id) => {
        formData.append('participantIds', id);
      });
    }

    // Add hashtags array
    if (eventData.hashtags && eventData.hashtags.length > 0) {
      eventData.hashtags.forEach((hashtag) => {
        formData.append('hashtags', hashtag);
      });
    }

    // Add communityEventId if provided
    if (eventData.communityEventId) {
      formData.append('communityEventId', eventData.communityEventId.toString());
    }

    const response = await api.post('/events', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  createWithPhoto: async (eventData) => {
    const formData = new FormData();
    formData.append('name', eventData.name);
    if (eventData.description) {
      formData.append('description', eventData.description);
    }
    formData.append('address', eventData.address);
    // Ensure latitude and longitude are sent as properly formatted numbers
    formData.append('latitude', eventData.latitude.toString());
    formData.append('longitude', eventData.longitude.toString());
    formData.append('date', eventData.date);
    formData.append('time', eventData.time);
    formData.append('eventType', eventData.eventType.toString());

    // Add suitableForPeople if provided
    if (eventData.suitableForPeople) {
      formData.append('suitableForPeople', eventData.suitableForPeople.toString());
    }

    // Add communityEventId if provided (for gatherings based on community events)
    if (eventData.communityEventId) {
      formData.append('communityEventId', eventData.communityEventId.toString());
    }

    // Add hashtags as array
    eventData.hashtags.forEach((hashtag) => {
      formData.append('hashtags', hashtag);
    });

    // Add empty friendIds array
    eventData.friendIds.forEach((friendId) => {
      formData.append('participantIds', friendId);
    });

    // Add photo if provided
    if (eventData.photo) {
      formData.append('photo', eventData.photo);
    }

    const response = await api.post('/events', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  updateParticipationStatus: async (eventId, status) => {
    // Convert string status to integer for backend
    const statusMap = {
      'confirmed': 1,
      'declined': -1,
      'pending': 0
    };
    const statusCode = statusMap[status] ?? status; // Use status directly if it's already a number
    const response = await api.put(`/events/${eventId}/participation`, { status: statusCode });
    return response.data;
  },

  endGathering: async (eventId) => {
    const response = await api.put(`/events/${eventId}/end`);
    return response.data;
  },

  leaveEvent: async (eventId) => {
    const response = await api.delete(`/events/${eventId}/leave`);
    return response.data;
  },

  joinEvent: async (eventId) => {
    const response = await api.post(`/events/${eventId}/join`);
    return response.data;
  },

  addParticipants: async (eventId, participantIds) => {
    const response = await api.post(`/events/${eventId}/participants`, { participantIds });
    return response.data;
  }
};

// Community Events API
export const communityEventsAPI = {
  getAll: async (status = null, eventType = null) => {
    const params = {};
    if (status !== null) params.status = status;
    if (eventType !== null) params.eventType = eventType;
    const response = await api.get('/communityevents', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/communityevents/${id}`);
    return response.data;
  },

  getNearby: async (latitude, longitude, radiusKm = 10, statuses = null, eventType = null, includePast = false, pastOnly = false, page = 1, pageSize = 10) => {
    const params = new URLSearchParams({ latitude, longitude, radiusKm, includePast, pastOnly, page, pageSize });
    if (statuses?.length) statuses.forEach(s => params.append('statuses', s));
    if (eventType !== null) params.append('eventType', eventType);
    const response = await api.get('/communityevents/nearby', { params });
    return response.data;
  },

  getUserFollowed: async (userId) => {
    const response = await api.get(`/communityevents/user/${userId}/followed`);
    return response.data;
  },

  create: async (eventData) => {
    const formData = new FormData();
    formData.append('name', eventData.name);
    if (eventData.description) {
      formData.append('description', eventData.description);
    }
    formData.append('address', eventData.address);
    formData.append('latitude', eventData.latitude.toString());
    formData.append('longitude', eventData.longitude.toString());
    if (eventData.date) {
      formData.append('date', eventData.date);
    }
    if (eventData.time) {
      formData.append('time', eventData.time);
    }
    formData.append('eventType', eventData.eventType.toString());

    if (eventData.suitableForPeople) {
      formData.append('suitableForPeople', eventData.suitableForPeople.toString());
    }

    if (eventData.minAttendance) {
      formData.append('minAttendance', eventData.minAttendance.toString());
    }
    if (eventData.maxAttendance) {
      formData.append('maxAttendance', eventData.maxAttendance.toString());
    }
    if (eventData.fixedAttendance) {
      formData.append('fixedAttendance', eventData.fixedAttendance.toString());
    }

    eventData.hashtags.forEach((hashtag) => {
      formData.append('hashtags', hashtag);
    });

    if (eventData.photo) {
      formData.append('photo', eventData.photo);
    }

    const response = await api.post('/communityevents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  vote: async (communityEventId, vote) => {
    const response = await api.put(`/communityevents/${communityEventId}/vote`, { vote });
    return response.data;
  },

  follow: async (communityEventId) => {
    const response = await api.post(`/communityevents/${communityEventId}/follow`);
    return response.data;
  },

  unfollow: async (communityEventId) => {
    const response = await api.delete(`/communityevents/${communityEventId}/follow`);
    return response.data;
  },

  end: async (communityEventId) => {
    const response = await api.put(`/communityevents/${communityEventId}/end`);
    return response.data;
  },

  getGatherings: async (communityEventId) => {
    const response = await api.get(`/communityevents/${communityEventId}/gatherings`);
    return response.data;
  },

  /**
   * Get pending approval events for the current user
   * Returns events that:
   * - Are pending (status 0) or dangerous (status -1)
   * - User hasn't voted on yet
   * - User didn't create
   * - Haven't passed their date (or have no date - proposals)
   */
  hasPendingUnapproved: async (userId) => {
    const response = await api.get(`/communityevents/user/${userId}/has-pending-unapproved`);
    return response.data;
  },

  getPendingApprovalEvents: async (userId, latitude, longitude, radiusKm = 10) => {
    // Fetch nearby community events – use large pageSize to get all events so
    // client-side filtering (status 0 + status -1) works across the full result set
    const result = await communityEventsAPI.getNearby(
      latitude,
      longitude,
      radiusKm,
      null,  // statuses - null means all statuses
      null,  // eventType - null means all types
      false, // includePast
      false, // pastOnly
      1,     // page
      500    // pageSize – large enough to cover all nearby events
    );

    // Filter to only pending (status 0) and dangerous (status -1) events
    // Exclude events with past dates (but keep events without dates - proposals)
    const now = new Date();
    const pendingEvents = result.items.filter(event => {
      // Check status and date conditions
      if (!(event.status === 0 || event.status === -1)) return false;

      // If event has no dateTime, it's a proposal - keep it
      if (!event.dateTime) return true;

      // Parse the event dateTime and check if it's in the future
      const eventDateTime = parseUTCDate(event.dateTime);
      return eventDateTime > now;
    });

    return pendingEvents;
  }
};

// Badges API
export const badgesAPI = {
  getGiveawayCount: async (userId) => {
    const response = await api.get(`/badges/user/${userId}/giveaway-count`);
    return response.data;
  },

  checkIfGiven: async (fromUserId, toUserId, eventId) => {
    const response = await api.get('/badges/check-given', {
      params: { fromUserId, toUserId, eventId }
    });
    return response.data;
  },

  giveBadge: async (recipientUserId, eventId, message, isAnonymous = false, isPrivate = false) => {
    const response = await api.post('/badges/give', {
      recipientUserId,
      eventId,
      message,
      isAnonymous,
      isPrivate
    });
    return response.data;
  },

  getReceivedBadges: async (userId) => {
    const response = await api.get(`/badges/user/${userId}/received`);
    return response.data;
  },

  getSentBadges: async (userId) => {
    const response = await api.get(`/badges/user/${userId}/sent`);
    return response.data;
  },

  getUnreactedCount: async (userId) => {
    const response = await api.get(`/badges/user/${userId}/unreacted-count`);
    return response.data;
  },

  setReaction: async (badgeId, reaction, isOffended = false) => {
    const response = await api.put(`/badges/${badgeId}/reaction`, { reaction, isOffended });
    return response.data;
  },

  purchaseBadges: async (quantity) => {
    const response = await api.post('/badges/purchase', { quantity });
    return response.data;
  }
};

// Pictures API
export const picturesAPI = {
  checkExists: async (fromUserId, connectedToUserId, eventId, communityEventId = null) => {
    const params = { fromUserId, connectedToUserId };
    if (communityEventId) params.communityEventId = communityEventId;
    else if (eventId) params.eventId = eventId;
    const response = await api.get(`/pictures/check`, { params });
    return response.data;
  },

  upload: async (file, picUserName, connectedToUserId, fromUserId, eventId = null, communityEventId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('PicUserName', picUserName);
    if (connectedToUserId != null) {
      formData.append('ConnectedToUserId', connectedToUserId); // omit for group event photos
    }
    formData.append('FromUserId', fromUserId);
    if (communityEventId) {
      formData.append('CommunityEventId', communityEventId);
    } else if (eventId) {
      formData.append('EventId', eventId);
    }

    const response = await axios.post(`${API_BASE_URL}/pictures/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  },

  getUserPictures: async (userId) => {
    const response = await api.get(`/pictures/user/${userId}`);
    return response.data;
  },

  getEventPictures: async (eventId) => {
    const response = await api.get(`/pictures/event/${eventId}`);
    return response.data;
  },

  getCommunityEventPictures: async (communityEventId) => {
    const response = await api.get(`/pictures/communityevent/${communityEventId}`);
    return response.data;
  },

  getImage: (sysFileName, username) => {
    return `${process.env.REACT_APP_IMAGE_BASE_URL}/${username}/${sysFileName}`;
  },

  delete: async (pictureId) => {
    const response = await api.delete(`/pictures/${pictureId}`);
    return response.data;
  },

  getUnapprovedCount: async (userId) => {
    const response = await api.get(`/pictures/user/${userId}/unapproved-count`);
    return response.data;
  },

  getUserPicturesAll: async (userId) => {
    const response = await api.get(`/pictures/user/${userId}/all`);
    return response.data;
  },

  approvePicture: async (pictureId) => {
    const response = await api.put(`/pictures/${pictureId}/approve`);
    return response.data;
  },

  disapprovePicture: async (pictureId) => {
    const response = await api.put(`/pictures/${pictureId}/disapprove`);
    return response.data;
  },

  softDelete: async (pictureId) => {
    const response = await api.delete(`/pictures/${pictureId}/soft-delete`);
    return response.data;
  }
};

export const personalFeedAPI = {
  getUserFeed: async (userId) => {
    const response = await api.get(`/personalfeed/user/${userId}`);
    return response.data;
  },

  create: async (feedData) => {
    const response = await api.post('/personalfeed', feedData);
    return response.data;
  },

  delete: async (feedId) => {
    const response = await api.delete(`/personalfeed/${feedId}`);
    return response.data;
  }
};

// Feed API (friends' feed)
export const feedAPI = {
  getUserFeed: async (userId) => {
    const response = await api.get(`/feed/user/${userId}`);
    return response.data;
  },

  markAsSeen: async (feedId) => {
    const response = await api.put(`/feed/${feedId}/seen`);
    return response.data;
  },

  getUnseenCount: async (userId) => {
    const response = await api.get(`/feed/user/${userId}/unseen-count`);
    return response.data;
  }
};

// Comments API
export const commentsAPI = {
  getEventComments: async (eventId) => {
    const response = await api.get(`/comments/event/${eventId}`);
    return response.data;
  },

  getCommunityEventComments: async (communityEventId) => {
    const response = await api.get(`/comments/communityevent/${communityEventId}`);
    return response.data;
  },

  create: async (commentData) => {
    const response = await api.post('/comments', commentData);
    return response.data;
  },

  update: async (commentId, commentData) => {
    const response = await api.put(`/comments/${commentId}`, commentData);
    return response.data;
  },

  delete: async (commentId) => {
    const response = await api.delete(`/comments/${commentId}`);
    return response.data;
  },

  vote: async (commentId, voteChange) => {
    const response = await api.put(`/comments/${commentId}/vote`, { vote: voteChange });
    return response.data;
  }
};

// Config API
export const configAPI = {
  getBadgeStoreConfig: async () => {
    const response = await api.get('/config/badge-store');
    return response.data;
  }
};

export default api;